/* eslint-disable max-lines */
import { useEffect, useMemo } from "react";
import { useWatch, useFormState } from "react-hook-form";
import { AlertCircle, Edit, Loader, Trash2 } from "lucide-react";
import { Card } from "@design-lab/shared/ui/card";
import { Text } from "@design-lab/shared/ui/text";
import { Button } from "@design-lab/shared/ui";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { trackDtfAiRedrawCancelled } from "@design-lab/shared/lib/analytics";
import type {
  ArtistInstructionsArtwork,
  LlmInterpretationState
} from "../../model/artist-instructions-types";
import { ARTIST_INSTRUCTIONS_STATES } from "../../model/artist-instructions-types";
import { useArtistInstructionsState } from "../../model/useArtistInstructionsState";
import { useArtistInstructionsActions } from "../../model/useArtistInstructionsActions";
import { QuestionsList } from "./QuestionsList";

export function ArtistInstructions() {
  const { form } = useArtworkForm();
  const artworkForm = useWatch({
    control: form.control
  });

  const side = artworkForm?.currentPlacement === "back" ? "back" : "front";
  const fieldName = `info.${side}.enhancementRequestNote` as const;

  const fieldState = useFormState({
    control: form.control,
    name: fieldName
  });

  const error = fieldState.errors?.info?.[side]?.enhancementRequestNote?.message;

  const artistInstructionsArtwork: ArtistInstructionsArtwork = useMemo(() => {
    const artworkInfo = artworkForm?.info?.[side];
    const placementData = artworkForm?.[side];

    const result: ArtistInstructionsArtwork = {
      id: Number(placementData?.artworkId),
      enhancementTier: artworkInfo?.enhancementTier || "custom",
      enhancementNote: artworkInfo?.enhancementRequestNote || ""
    };

    // Add optional properties only if they exist
    if ((artworkInfo as any)?.llmInterpretationState) {
      result.llmInterpretationState = (artworkInfo as any)
        .llmInterpretationState as LlmInterpretationState;
    }
    if (placementData?.llmRedrawRequestId) {
      result.llmRedrawRequestId = placementData.llmRedrawRequestId;
    }

    return result;
  }, [artworkForm, side]);

  const handleArtistInstructionsStateChange = (data: {
    llmInterpretationState: LlmInterpretationState;
    enhancementNote?: string;
    llmRedrawRequestId?: number;
  }) => {
    const { enhancementNote, llmInterpretationState, llmRedrawRequestId } = data;
    form.setValue(`info.${side}.llmInterpretationState` as any, llmInterpretationState);

    form.setValue(
      fieldName,
      llmInterpretationState.currentState === ARTIST_INSTRUCTIONS_STATES.FINAL
        ? enhancementNote || ""
        : ""
    );
    form.trigger(fieldName); // Trigger validation to clear errors
    if (llmRedrawRequestId) {
      form.setValue(`${side}.llmRedrawRequestId` as any, llmRedrawRequestId);
    }
  };

  const handleArtistInstructionsDelete = () => {
    resetLocalState();
    // Reset component state to allow re-entry
    setInstructions("");
    setQuestions([]);
    setCurrentState(ARTIST_INSTRUCTIONS_STATES.INITIAL);
    setLlmRedrawRequestId(null);
  };

  const resetLocalState = () => {
    // Clear form values
    form.setValue(fieldName, "");
    form.setValue(`info.${side}.llmInterpretationState` as any, undefined);
    form.setValue(`${side}.llmRedrawRequestId` as any, undefined);
  };

  const {
    instructions,
    setInstructions,
    questions,
    setQuestions,
    currentState,
    setCurrentState,
    llmRedrawRequestId,
    setLlmRedrawRequestId
  } = useArtistInstructionsState(artistInstructionsArtwork);

  const { handleSubmit, createRequestInProgress, updateRequestInProgress, hasError } =
    useArtistInstructionsActions({
      artistInstructionsArtwork,
      instructions,
      questions,
      setQuestions,
      currentState,
      setCurrentState,
      llmRedrawRequestId,
      setLlmRedrawRequestId,
      handleArtistInstructionsStateChange
    });

  const isLoading = createRequestInProgress || updateRequestInProgress;

  useEffect(() => {
    form.trigger(fieldName);
  }, []);

  const isTyping = currentState === ARTIST_INSTRUCTIONS_STATES.TYPING;
  const wasTyping =
    artistInstructionsArtwork?.llmInterpretationState?.previousState ===
    ARTIST_INSTRUCTIONS_STATES.TYPING;
  const isQuestions = currentState === ARTIST_INSTRUCTIONS_STATES.QUESTIONS;
  const isFinal = currentState === ARTIST_INSTRUCTIONS_STATES.FINAL;

  const isSubmitDisabled = !instructions.length || instructions.length < 10 || isLoading;

  const handleDelete = () => {
    const placementData = artworkForm?.[side];
    if (placementData?.artworkId) {
      trackDtfAiRedrawCancelled(Number(placementData.artworkId), currentState);
    }
    handleArtistInstructionsDelete();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between gap-4">
        <Text className="text-md">
          Our Jiffy Artists will enhance your design based on the instructions you provide.
        </Text>
        {!isTyping && !isFinal && (
          <Button
            data-testid="artist-instructions-delete-button"
            variant="icon"
            className="hover:bg-transparent"
            onPress={handleDelete}
          >
            <Trash2 size={20} />
          </Button>
        )}
      </div>

      {(isTyping || (wasTyping && isQuestions)) && (
        <div className="flex flex-col gap-2">
          <Text className="text-sm">What edits would you like made to your design?</Text>
          <Textarea
            data-testid="artist-instructions-textarea"
            rows={4}
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            placeholder='Example: "Change the font color to black. Remove the background from the top half of the image."'
            className="disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isLoading || !isTyping}
          />
          {!isTyping && wasTyping && (
            <Button
              data-testid="edit-instructions-button"
              className="flex w-full items-center gap-2"
              variant="icon"
              onPress={() => {
                setCurrentState(ARTIST_INSTRUCTIONS_STATES.TYPING);
                resetLocalState();
              }}
            >
              <Edit size={16} /> Edit Instructions
            </Button>
          )}
          <Button
            data-testid="submit-instructions-button"
            className="w-full"
            size="lg"
            isDisabled={isSubmitDisabled}
            onPress={() => handleSubmit()}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader size={20} className="animate-spin" />
                Submitting...
              </div>
            ) : (
              "Submit Instructions"
            )}
          </Button>
        </div>
      )}

      {isQuestions && (
        <QuestionsList questions={questions} onSubmit={handleSubmit} isLoading={isLoading} />
      )}

      {(isFinal || hasError) && (
        <Card className="flex flex-col gap-4 border-green-500 bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} className="text-green-600" />
            <h3 className="font-medium text-green-900">Instructions Captured</h3>
          </div>
          <p className="text-sm text-green-800">
            Our Jiffy Artists have received your instructions and will enhance your design
            accordingly.
          </p>
        </Card>
      )}

      {hasError && (
        <div className="border-destructive/90 flex items-center gap-2 rounded-md border p-4">
          <AlertCircle className="text-destructive size-4 min-w-4" />
          <p className="text-destructive text-sm">
            There was an error processing your request. Please try again.
          </p>
        </div>
      )}

      {error && !isFinal && <small className="text-red-500">{error}</small>}
    </div>
  );
}
